import { useState } from "react";
import { Mail, Lock, LogIn, UserPlus, Loader2 } from "lucide-react";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";

type Mode = "login" | "signup";

export function AuthPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);


  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const ref = new URL(SUPABASE_URL).hostname.split(".")[0];
      const path = mode === "signup" ? "/auth/v1/signup" : "/auth/v1/token?grant_type=password";
      const body: Record<string, unknown> = { email, password };
      const r = await fetch(SUPABASE_URL + path, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error_description || j.msg || j.error || `認証に失敗しました (${r.status})`);
      if (!j.access_token) {
        setInfo("確認メールを送信しました。メール内のリンクを開いて登録を完了してください。");
        return;
      }
      localStorage.setItem(`sb-${ref}-auth-token`, JSON.stringify(j));
      window.location.replace(window.location.origin);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "認証に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const oauth = async (provider: "google" | "line") => {
    setError(null);
    setInfo(null);
    if (provider === "line") {
      setInfo("LINEは管理者画面では使用しません。");
      return;
    }
    window.location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=${provider}&redirect_to=${encodeURIComponent(window.location.origin + "/")}`;
  };

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-10 bg-[var(--color-bg)] text-[var(--color-fg)]">
      <div className="mx-auto w-full max-w-sm space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold">sitame 管理者</h1>
          <p className="text-sm text-ink-soft dark:text-white/70">支良洲工業 管理者ダッシュボード</p>
        </div>

        <div className="grid grid-cols-2 rounded-xl border border-[var(--color-border)] overflow-hidden text-sm">
          <button onClick={() => setMode("login")} className={`py-2 ${mode === "login" ? "bg-brand-500 text-white" : ""}`}>ログイン</button>
          <button onClick={() => setMode("signup")} className={`py-2 ${mode === "signup" ? "bg-brand-500 text-white" : ""}`}>新規登録</button>
        </div>

        <div className="space-y-2">
          <button onClick={() => oauth("google")} className="w-full py-3 rounded-xl border border-[var(--color-border)] flex items-center justify-center gap-2 text-sm font-medium hover:bg-brand-50 dark:hover:bg-brand-800/30">
            <span className="font-bold text-[#4285F4]">G</span> Googleで続ける
          </button>
          <button onClick={() => oauth("line")} className="w-full py-3 rounded-xl border border-[var(--color-border)] flex items-center justify-center gap-2 text-sm font-medium hover:bg-brand-50 dark:hover:bg-brand-800/30">
            <span className="font-bold text-[#06C755]">LINE</span> で続ける
          </button>
        </div>

        <div className="flex items-center gap-3 text-xs text-ink-soft dark:text-white/50">
          <span className="flex-1 h-px bg-[var(--color-border)]" />または<span className="flex-1 h-px bg-[var(--color-border)]" />
        </div>

        <form onSubmit={submitEmail} className="space-y-3">
          <label className="block text-sm space-y-1">
            <span className="flex items-center gap-1 font-medium"><Mail size={14} /> メールアドレス</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
              className="w-full border border-[var(--color-border)] rounded-md px-3 py-2 bg-[var(--color-bg)]" placeholder="you@example.com" />
          </label>
          <label className="block text-sm space-y-1">
            <span className="flex items-center gap-1 font-medium"><Lock size={14} /> パスワード</span>
            <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              className="w-full border border-[var(--color-border)] rounded-md px-3 py-2 bg-[var(--color-bg)]" placeholder="8文字以上" />
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}
          {info && <p className="text-sm text-brand-600 dark:text-brand-300">{info}</p>}

          <button type="submit" disabled={busy} className="w-full py-3 rounded-xl bg-brand-500 text-white flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : mode === "signup" ? <UserPlus size={16} /> : <LogIn size={16} />}
            {mode === "signup" ? "業者アカウント作成" : "ログイン"}
          </button>
        </form>
        <p className="text-[11px] text-center text-ink-soft dark:text-white/50">
          管理者アカウントでログインしてください。
        </p>
      </div>
    </div>
  );
}
